import type { ReactNode } from "react"
import LoaderAtm from "@onestore/hel/dist/components/atoms/LoaderAtm"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import _t from "@core/i18n"
import { PeriodInfo } from "@core/period-info"
import {
  formatted,
  getLowestPriceDiscountPercent,
  getTaxRate,
} from "@core/pricing"
import { PriceFormat, PriceType } from "@core/types"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import {
  type Cell,
  CellType,
  type HorizontalProductBox,
} from "@gatsby-plugin-generic-page/fragments/horizontalProductBox"
import { getUpsellData } from "@gatsby-plugin-generic-page/helpers/getUpsellData"
import { stripMarkdownText } from "@gatsby-plugin-generic-page/helpers/stripMarkdownText"
import { useConfigsHookContext } from "@gatsby-plugin-generic-page/sections/TableHorizontalBoxesSection"
import { typeMatches } from "@onestore-graphql"
import { get, isEmpty } from "lodash"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import url from "~/lib/url"
import NameCell from "./NameCell"
import PriceCell from "./PriceCell"
import ResourceCategoryCell, {
  getResourceCategoryCellString,
} from "./ResourceCategoryCell"
import TextCell from "./TextCell"

export type HorizontalTableCellsType = {
  type: CellType
  title?: string
  text: ReactNode
  textWithoutStyles: string
  widthPercent?: number
  buttonHref?: string
}

export function useHorizontalTableCells(
  productData?: HorizontalProductBox,
  withoutStyles: boolean = false
): HorizontalTableCellsType[] | undefined {
  const { currentPriceType } = usePriceTypeContext()

  const {
    columns,
    configsValues,
    isCalculateProductsLoading,
    getHasProductActiveResourceCategories,
    getCalculateProductPrices,
    getUrlConfigsValues,
    getResourceCategory,
    getProductResource,
    getProductPeriodResource,
    getConfigResource,
  } = useConfigsHookContext() ?? {}

  if (isEmpty(productData)) {
    return
  }

  const {
    saleConfiguration,
    cells,
    labelElement,
    promotedHorizontalProductBox,
  } = productData.flatData

  if (
    !getUrlConfigsValues ||
    !getHasProductActiveResourceCategories ||
    !getHasProductActiveResourceCategories(productData.id)
  ) {
    return
  }

  const urlConfigsValues = getUrlConfigsValues(productData.id)

  const columnsCount = columns ? columns.length : 0

  if (cells.length !== columnsCount || isEmpty(saleConfiguration)) {
    return
  }

  const plan = get(saleConfiguration[0], "flatData.defaultCloudBluePlan[0]")

  if (isEmpty(plan)) {
    return
  }

  const calculatePrices = getCalculateProductPrices
    ? getCalculateProductPrices(plan.flatData.id)
    : null

  const productPlanPeriods = plan.flatData.periods

  const selectedPeriodObject = configsValues
    ? productPlanPeriods.find(
        (period) => period.period_name === configsValues.period
      )
    : null

  const periodInfo = selectedPeriodObject
    ? new PeriodInfo(selectedPeriodObject)
    : undefined

  if (!periodInfo) {
    return
  }

  const priceInfo = plan.flatData.priceInfo || undefined

  const currency = periodInfo.getFormatted(PriceFormat.CURRENCY)

  const { priceValue, lowestPrice, lowestPricePercent } = periodInfo.getPrices(
    currentPriceType,
    true
  )

  const hasPromotion = periodInfo.hasPromotion() ?? false

  const periodName = periodInfo.period.period_name

  const priceTypeText =
    currentPriceType === PriceType.NETTO
      ? _t("prices.netto")
      : _t("prices.gross")

  const buttonHref = url.generateBonusUrl({
    p: {
      alias: plan.flatData.alias,
      parameters: !isEmpty(urlConfigsValues.parameters)
        ? urlConfigsValues.parameters
        : undefined,
      resources: !isEmpty(urlConfigsValues.resources)
        ? urlConfigsValues.resources
        : undefined,
    },
    ax: getUpsellData(saleConfiguration[0].flatData.upsell),
    period: periodName,
    priceType: currentPriceType,
  })

  const cellTextPlaceholder = (
    <MarkdownText
      typography={{
        default: "small2",
        bold: "small3",
      }}
      pushSpace={0}
    >
      -
    </MarkdownText>
  )

  return cells.map((cell: Cell, index: number) => {
    let cellObject = {
      type: cell.type,
      title: columns ? columns[index].label : undefined,
      text: cellTextPlaceholder,
      textWithoutStyles: "",
      widthPercent: columns ? columns[index].widthPercent : undefined,
      buttonHref: withoutStyles ? buttonHref : undefined,
    }

    if (typeMatches(cell.type, CellType.NAME) && !isEmpty(plan)) {
      cellObject.text = (
        <NameCell
          cbName={plan.flatData.name}
          value={cell.value}
          labelElement={labelElement}
          hasPromotion={hasPromotion}
        />
      )
      cellObject.textWithoutStyles = cell.value || plan.flatData.name
    } else if (typeMatches(cell.type, CellType.PRICE)) {
      const hasCalulatePrices = !isEmpty(calculatePrices)

      const updatePiceValue = hasCalulatePrices
        ? formatted(calculatePrices.regularPrice, currentPriceType).full
        : priceValue

      const updatePromoValue =
        hasCalulatePrices && !isEmpty(calculatePrices.promoPrice)
          ? formatted(calculatePrices.promoPrice, currentPriceType).full
          : null

      const updateLowestPrice =
        hasCalulatePrices && !isEmpty(calculatePrices.lowestPurchasePrice)
          ? formatted(calculatePrices.lowestPurchasePrice, currentPriceType)
              .full
          : lowestPrice

      const updateLowestPricePercent =
        hasCalulatePrices && !isEmpty(calculatePrices.lowestPurchasePrice)
          ? getLowestPriceDiscountPercent(
              calculatePrices.promoPrice
                ? {
                    ...calculatePrices.promoPrice,
                    taxRate: calculatePrices.promoPrice.taxRate || getTaxRate(),
                  }
                : {
                    ...calculatePrices.regularPrice,
                    taxRate:
                      calculatePrices.regularPrice.taxRate || getTaxRate(),
                  },
              {
                ...calculatePrices.lowestPurchasePrice,
                taxRate:
                  calculatePrices.lowestPurchasePrice.taxRate || getTaxRate(),
              }
            )
          : lowestPricePercent

      const { renewPrice } = periodInfo.getPrices(currentPriceType, true)

      const renewSuffix = `${_t(
        `periodName.renewPrice.${periodInfo.getPeriodType()}`,
        {
          smart_count: periodInfo.getPeriodLength(),
        }
      )}`

      const updateRenewalPriceValue =
        !isEmpty(calculatePrices) && !isEmpty(calculatePrices.renewalPrice)
          ? formatted(calculatePrices.renewalPrice, currentPriceType).full
          : renewPrice

      const renewalPriceText = isCalculateProductsLoading
        ? undefined
        : `${updateRenewalPriceValue} ${priceTypeText} ${renewSuffix}`

      const priceItem = (
        <PriceCell
          hasPromotion={hasPromotion}
          priceValue={updatePromoValue || updatePiceValue}
          lowestPrice={updateLowestPrice}
          lowestPricePercent={updateLowestPricePercent}
          renewalPrice={updateRenewalPriceValue}
          renewalPriceText={renewalPriceText}
          buttonHref={buttonHref}
          currency={currency}
          priceTypeText={priceTypeText}
          productData={productData}
          promotedHorizontalProductBox={promotedHorizontalProductBox}
          priceInfo={priceInfo}
          periodInfo={periodInfo}
          withoutButton={withoutStyles}
        />
      )

      cellObject.text = isCalculateProductsLoading ? (
        <AlignContentOrg horizontalAlign="right">
          <LoaderAtm size="small" />
        </AlignContentOrg>
      ) : (
        priceItem
      )
    } else if (typeMatches(cell.type, CellType.TEXT) && cell.value) {
      cellObject.text = <TextCell value={cell.value} icon={cell.icon} />
      cellObject.textWithoutStyles = stripMarkdownText(cell.value)
    } else if (
      typeMatches(cell.type, CellType.RESOURCE_CATEGORY) &&
      !isEmpty(cell.resourceCategory) &&
      cell.resourceCategory
    ) {
      const resourceCategoryId = cell.resourceCategory[0].flatData.remoteId

      cellObject.text = (
        <ResourceCategoryCell
          productId={productData.id}
          resourceCategoryId={resourceCategoryId.toString()}
        />
      )
      cellObject.textWithoutStyles = getResourceCategoryCellString(
        productData.id,
        resourceCategoryId.toString(),
        getResourceCategory,
        getProductResource,
        getProductPeriodResource,
        getConfigResource
      )
    }

    return cellObject
  })
}
